import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import {
	usePassIds,
	useStandsLookup,
	usePassTypesLookup,
	usePassShapesLookup,
} from "../hooks/usePassIds";
import { usePassIdMutation } from "../hooks/usePassIdMutation";
import type { PassId } from "../models/PassId";
import { PASS_ID_INITIAL_VALUES } from "../models/PassId";
import { PassIdsGrid } from "./PassIdsGrid";
import { PassIdDetailViewer } from "./PassIdDetailViewer";
import { PassIdsToolbar } from "./PassIdsToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

// Default layout percentages
const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	standId: urlParam.int(),
	passTypeId: urlParam.int(),
	passShapeId: urlParam.int(),
	passId: urlParam.int(),
};

export const PassIdsManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{
			search,
			onlyActives,
			standId,
			passTypeId,
			passShapeId,
			passId: urlPassId,
		},
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	// A link to a pass shape that is no longer offered falls back to all of them.
	const { data: passShapeOptions = [], isSuccess: passShapeOptionsLoaded } =
		usePassShapesLookup();
	useDropUnknownValue(
		passShapeId,
		passShapeOptionsLoaded,
		passShapeOptions.map((option) => option.Id),
		useCallback(() => dropValues("passShapeId"), [dropValues]),
	);
	// A link to a pass type that is no longer offered falls back to all of them.
	const { data: passTypeOptions = [], isSuccess: passTypeOptionsLoaded } =
		usePassTypesLookup();
	useDropUnknownValue(
		passTypeId,
		passTypeOptionsLoaded,
		passTypeOptions.map((option) => option.Id),
		useCallback(() => dropValues("passTypeId"), [dropValues]),
	);
	// A link to a stand that is no longer offered falls back to all of them.
	const { data: standOptions = [], isSuccess: standOptionsLoaded } =
		useStandsLookup();
	useDropUnknownValue(
		standId,
		standOptionsLoaded,
		standOptions.map((option) => option.Id),
		useCallback(() => dropValues("standId"), [dropValues]),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [passIdToDelete, setPassIdToDelete] = useState<PassId | null>(null);

	// Layout persistence
	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "pass-ids-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	// Data fetching
	const {
		passIds: allPassIds,
		isLoading,
		error: queryError,
	} = usePassIds({
		standId,
		passTypeId,
		passShapeId,
	});

	// Client-side search filtering
	const passIds = useMemo(() => {
		const base = onlyActives ? allPassIds.filter((p) => p.Active) : allPassIds;
		if (!searchText || searchText.trim() === "") {
			return base;
		}
		const searchLower = searchText.toLowerCase();
		return base.filter(
			(p) =>
				p.Name?.toLowerCase().includes(searchLower) ||
				p.Description?.toLowerCase().includes(searchLower) ||
				p.StandName?.toLowerCase().includes(searchLower) ||
				p.ExtraStandNames?.toLowerCase().includes(searchLower) ||
				p.PassTypeName?.toLowerCase().includes(searchLower) ||
				p.PassShapeName?.toLowerCase().includes(searchLower),
		);
	}, [allPassIds, searchText, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<PassId>({
		id: urlPassId,
		setId: (value) => setFilters({ passId: value }),
		dropId: () => dropValues("passId"),
		rows: allPassIds,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedPassId = selection.current;

	// Mutations
	const { savePassId, deletePassId, isSaving, isDeleting } = usePassIdMutation({
		onSuccess: (passId) => {
			const isNew = selection.isNew;
			setNotification(
				isNew ? "Pass ID created successfully" : "Pass ID updated successfully",
			);
			if (passId) {
				selection.adopt(passId);
			}
		},
		onError: (err) => setError(err.message),
		onDeleteSuccess: () => {
			setNotification("Pass ID deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setPassIdToDelete(null);
		},
	});

	// Handlers
	const handleRowSelected = useCallback(
		(passId: PassId | null) => {
			if (passId) selection.select(passId);
			else selection.clear();
		},
		[selection],
	);

	const handleAddNew = useCallback(() => {
		// Pre-fill from active filters
		const newPassId = { ...PASS_ID_INITIAL_VALUES };
		if (standId) newPassId.StandId = standId;
		if (passTypeId) newPassId.PassTypeId = passTypeId;
		if (passShapeId) newPassId.PassShapeId = passShapeId;
		selection.startNew(newPassId);
	}, [standId, passTypeId, passShapeId, selection]);

	const handleSave = useCallback(
		(passId: PassId) => {
			savePassId(passId);
		},
		[savePassId],
	);

	const handleDelete = useCallback((passId: PassId) => {
		setPassIdToDelete(passId);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (passIdToDelete?.Id) {
			deletePassId(passIdToDelete.Id);
		}
	}, [passIdToDelete, deletePassId]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setPassIdToDelete(null);
	}, []);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
				Pass IDs
			</Typography>

			<PassIdsToolbar
				search={searchText}
				onSearchChange={setSearchText}
				standId={standId}
				onStandChange={(value) => setFilters({ standId: value })}
				passTypeId={passTypeId}
				onPassTypeChange={(value) => setFilters({ passTypeId: value })}
				passShapeId={passShapeId}
				onPassShapeChange={(value) => setFilters({ passShapeId: value })}
				onlyActives={onlyActives}
				onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
				onAdd={handleAddNew}
			/>

			<Group
				id="pass-ids-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={1}
							sx={{
								flex: 1,
								width: "100%",
								overflow: "hidden",
							}}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<PassIdsGrid
									passIds={passIds}
									selectedPassId={selectedPassId?.Id ? selectedPassId : null}
									onSelectPassId={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": {
									backgroundColor: "#fff",
								},
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`grip-dot-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={1}
							sx={{
								height: "100%",
								overflowY: "scroll",
								overflowX: "hidden",
							}}
						>
							<PassIdDetailViewer
								passId={selectedPassId}
								existingPassIds={allPassIds}
								isSaving={isSaving}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE PASS ID"
				itemName={passIdToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
